import React, { type Ref, type SyntheticEvent, useEffect } from 'react';

import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { useIntl } from 'react-intl-next';

import {
	type UIAnalyticsEvent,
	useAnalyticsEvents as useAllAnalyticsEvents,
} from '@atlaskit/analytics-next';
import AddIcon from '@atlaskit/icon/core/add';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { type TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';

import { isAnalyticsEventsMigrated } from '../../../../common/utils/statsig';
import { useTeamBiDirectionalExp } from '../../../team-selector/hooks/use-team-connection-exp';

import messages from './messages';

const engagementId = 'team-button-onboarding';

type TeamButtonTriggerProps = {
	triggerProps: TriggerProps | { ref: Ref<any> };
	numberOfTeams: number;
	onClick: (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

const buttonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	display: 'flex',
	alignItems: 'center',
	height: '32px',
	width: '32px',
	justifyContent: 'center',
	color: 'color.text.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const buttonStylesWithNumber = xcss({
	width: '56px',
});

export const TeamButtonTrigger = ({
	triggerProps,
	numberOfTeams,
	onClick,
}: TeamButtonTriggerProps) => {
	const { formatMessage } = useIntl();
	const { fireEvent: DEPRECATED_fireEvent } = usePeopleTeamAnalyticsEvents();
	const { fireEvent } = useAnalyticsEvents();
	const { createAnalyticsEvent } = useAllAnalyticsEvents();
	const { isTeamBiDirectionalEnabled } = useTeamBiDirectionalExp();

	useEffect(() => {
		const result = createAnalyticsEvent({})?.context.find(
			(item) => item.attributes && item.attributes.scope !== undefined,
		);
		if (isAnalyticsEventsMigrated()) {
			fireEvent('ui.teamButton.viewed', {
				teamsCount: numberOfTeams,
				scope: result?.attributes?.scope ?? '',
				version: isTeamBiDirectionalEnabled ? 'm2' : 'm1',
			});
		} else {
			DEPRECATED_fireEvent('ui.teamButton.viewed', {
				teamsCount: numberOfTeams,
				scope: result?.attributes?.scope ?? '',
				version: isTeamBiDirectionalEnabled ? 'm2' : 'm1',
			});
		}
	}, [
		createAnalyticsEvent,
		fireEvent,
		DEPRECATED_fireEvent,
		isTeamBiDirectionalEnabled,
		numberOfTeams,
	]);

	const content = (
		<Pressable
			{...triggerProps}
			onClick={onClick}
			xcss={[buttonStyles, numberOfTeams !== 1 && buttonStylesWithNumber]}
			testId="team-button-trigger"
		>
			<TeamsIcon color="currentColor" label={formatMessage(messages.iconLabel)} />
			{numberOfTeams > 1 && (
				<Box as="span" paddingInlineStart="space.100">
					{numberOfTeams}
				</Box>
			)}
			{numberOfTeams === 0 && (
				<Box as="span" paddingInlineStart="space.050">
					<AddIcon color="currentColor" label={formatMessage(messages.addTeamIconLabel)} />
				</Box>
			)}
		</Pressable>
	);

	if (!isTeamBiDirectionalEnabled) {
		return content;
	}

	return (
		<SpotlightManager>
			<SpotlightTarget name={engagementId}>{content}</SpotlightTarget>
			<EngagementSpotlight name={engagementId} />
		</SpotlightManager>
	);
};
